import React, { useEffect, useRef, useState, useLayoutEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { observer } from "mobx-react";
import HomePageChatWindow from '../../../../../components/DocumentCanvas/HomePageChatWindow';
import { ChatInput } from '../../../../../elements/v2/Input';
import { ChatBoxContainer, ChatMessagesWrapper, ChatInputWrapper } from '../../../../../elements/ChatBox/ChatBox.styled';
import dayjs from 'dayjs'
import { useStore, useWebSocketScope } from '../../../../../hooks';
import { useChatContext } from '../../context/ChatContext';
import { useChatCommunication } from '../../hooks/useChatCommunication';
import { WebSocketScope } from '../../../../../utils/enums';

const ChatBoxPersistentHTTP = observer(forwardRef(({ customerSelected, agentId, agentDomain, inputWidth, showNewChatButton, metadata = {}, initialMessage = null, handleMetadata, agentData = {}, onMessagesUpdate = null }, ref) => {
    const { authStore, conversationsStore } = useStore()
    const chatContext = useChatContext();
    const { sendMessage, subscribeToResponse, isConnected, connectionError, retryConnection } = useChatCommunication(agentId);
    
    const [newMessage, setNewMessage] = useState('');
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedAgentData, setSelectedAgentData] = useState({});
    const [agent_params, setAgent_params] = useState({
        message: "",
        campaign_id: "",
        campaign_version: "",
        agent: ""
    });
    const [attachment, setAttachment] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [regeneratingMessageIndex, setRegeneratingMessageIndex] = useState(null);
    const bottomRef = useRef(null);
    const textAreaRef = useRef();
    const streamingMessagesRef = useRef(new Map()); // Track streaming messages by messageId
    const streamingChunksRef = useRef(new Map()); // Track chunks by messageId -> Map<chunkIndex, chunkContent>
    const expectedChunksRef = useRef(new Map()); // Track expected chunk count by messageId (from COMPLETE event)
    
    customerSelected && conversationsStore.setCustomerId(customerSelected);

    // Obtener la conversación actual del contexto
    const conversation = chatContext.getConversation(agentId);
    
    // Obtener los valores del contexto directamente para asegurar reactividad
    const messages = conversation.messages;
    const agentIsTyping = conversation.agentIsTyping;
    const conversationID = conversation.conversationID;
    const showStopButton = conversation.showStopButton;
    const isSendMessageClicked = conversation.isSendMessageClicked;
    const hasMessages = conversation.hasMessages;

    // Helper function to check if all chunks are received and finalize the message
    const checkAndFinalizeMessage = useCallback((messageId, conversationUuid) => {
        const expectedCount = expectedChunksRef.current.get(messageId);
        const chunksMap = streamingChunksRef.current.get(messageId);
        
        if (!expectedCount || !chunksMap) return false;
        
        // Check if we have all chunks (0 to expectedCount-1)
        if (chunksMap.size >= expectedCount) {
            const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
            const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
            
            // Update message as complete
            const currentMessages = [...messages];
            const existingIndex = currentMessages.findIndex(m => m.messageId === messageId);
            
            if (existingIndex >= 0) {
                currentMessages[existingIndex] = { 
                    ...currentMessages[existingIndex], 
                    message: reconstructedContent,
                    isStreaming: false 
                };
                chatContext.setMessages(agentId, currentMessages);
            }
            
            // Cleanup refs
            streamingMessagesRef.current.delete(messageId);
            streamingChunksRef.current.delete(messageId);
            expectedChunksRef.current.delete(messageId);
            chatContext.setAgentIsTyping(agentId, false);
            chatContext.setIsSendMessageClicked(agentId, false);
            setIsLoading(false);
            
            return true;
        }
        
        return false;
    }, [messages, agentId, chatContext]);

    // WebSocket subscription for CONVERSATION scope with DELTA event
    // Relies on backend sending isLastChunk: true when streaming is complete
    useWebSocketScope(
        WebSocketScope.CONVERSATION,
        useCallback((message) => {
            const payload = message.payload || {};
            const { chunkContent, chunkIndex, isLastChunk, conversationUuid, messageId } = payload;
            
            // Strict filter by conversation - only accept messages for our conversation
            // If we have a conversation ID, only accept messages for that conversation
            if (conversationID) {
                if (!conversationUuid || conversationUuid !== conversationID) {
                    return;
                }
            } else {
                // If we don't have a conversation yet, don't accept any WebSocket messages
                // (we'll get the conversation ID from the send message response)
                return;
            }
            
            // Handle empty content with isLastChunk (cleanup signal)
            if (!chunkContent) {
                if (isLastChunk && messageId) {
                    streamingMessagesRef.current.delete(messageId);
                    streamingChunksRef.current.delete(messageId);
                    chatContext.setAgentIsTyping(agentId, false);
                    chatContext.setIsSendMessageClicked(agentId, false);
                    setIsLoading(false);
                }
                return;
            }
            
            // Initialize tracking for new message
            if (!streamingChunksRef.current.has(messageId)) {
                streamingChunksRef.current.set(messageId, new Map());
                streamingMessagesRef.current.set(messageId, '');
                chatContext.setAgentIsTyping(agentId, false);
            }
            
            // Store chunk by index
            const chunksMap = streamingChunksRef.current.get(messageId);
            const effectiveIndex = typeof chunkIndex === 'number' ? chunkIndex : chunksMap.size;
            chunksMap.set(effectiveIndex, chunkContent);
            
            // Reconstruct content in order (sorted by chunk index)
            const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
            const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
            streamingMessagesRef.current.set(messageId, reconstructedContent);
            
            // Update message in context
            const currentMessages = [...messages];
            const existingIndex = currentMessages.findIndex(m => m.messageId === messageId);
            
            const messageData = {
                message: reconstructedContent,
                type: 'received',
                time: dayjs().format('HH:mm'),
                messageId: messageId,
                conversationUuid: conversationUuid,
                isStreaming: !isLastChunk,
            };
            
            if (existingIndex >= 0) {
                currentMessages[existingIndex] = { ...currentMessages[existingIndex], ...messageData };
            } else {
                currentMessages.push(messageData);
            }
            
            chatContext.setMessages(agentId, currentMessages);
            
            // Scroll to bottom during streaming
            setTimeout(() => scrollToBottom(), 50);
            
            // Check if all chunks are now received (in case COMPLETE arrived first)
            checkAndFinalizeMessage(messageId, conversationUuid);
        }, [conversationID, agentId, messages, checkAndFinalizeMessage]),
        'DELTA'
    );
    
    // WebSocket subscription for CONVERSATION scope with COMPLETE event
    // Stores the expected chunk count and the final chunk, then checks if all chunks are received
    useWebSocketScope(
        WebSocketScope.CONVERSATION,
        useCallback((message) => {
            const payload = message.payload || {};
            const { chunkContent, chunkIndex, conversationUuid, messageId } = payload;
            
            // Strict filter by conversation
            if (conversationID) {
                if (!conversationUuid || conversationUuid !== conversationID) {
                    return;
                }
            } else {
                return;
            }
            
            if (!messageId) return;
            
            // Store the expected total chunk count (chunkIndex + 1 since indices are 0-based)
            if (typeof chunkIndex === 'number') {
                expectedChunksRef.current.set(messageId, chunkIndex + 1);
            }
            
            // Initialize tracking for new message if needed
            if (!streamingChunksRef.current.has(messageId)) {
                streamingChunksRef.current.set(messageId, new Map());
                streamingMessagesRef.current.set(messageId, '');
                chatContext.setAgentIsTyping(agentId, false);
            }
            
            // Store chunk content if present (COMPLETE event often has the last chunk)
            if (chunkContent) {
                const chunksMap = streamingChunksRef.current.get(messageId);
                const effectiveIndex = typeof chunkIndex === 'number' ? chunkIndex : chunksMap.size;
                chunksMap.set(effectiveIndex, chunkContent);
                
                const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
                const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
                streamingMessagesRef.current.set(messageId, reconstructedContent);
                
                // Update message in state (still may be streaming if other chunks pending)
                const currentMessages = [...messages];
                const existingIndex = currentMessages.findIndex(m => m.messageId === messageId);
                
                const messageData = {
                    message: reconstructedContent,
                    type: 'received',
                    time: dayjs().format('HH:mm'),
                    messageId: messageId,
                    conversationUuid: conversationUuid,
                    isStreaming: true, // Will be set to false by checkAndFinalizeMessage
                };
                
                if (existingIndex >= 0) {
                    currentMessages[existingIndex] = { ...currentMessages[existingIndex], ...messageData };
                } else {
                    currentMessages.push(messageData);
                }
                
                chatContext.setMessages(agentId, currentMessages);
            }
            
            // Check if all chunks are now received
            checkAndFinalizeMessage(messageId, conversationUuid);
        }, [conversationID, agentId, messages, chatContext, checkAndFinalizeMessage]),
        'COMPLETE'
    );

    useEffect(() => {
        if (onMessagesUpdate) {
            onMessagesUpdate(messages);
        }
    }, [messages, onMessagesUpdate]);

    useEffect(() => {
        if (agentId) {
            setSelectedAgent({ id: agentId, domain: agentDomain });
            setAgent_params((prevParams) => ({
                ...prevParams,
                agent: agentId
            }));
        }
    }, [agentId])

    useEffect(() => {
        if (Object.keys(agentData).length) {
            setSelectedAgentData(agentData)
        }
    }, [agentData])

    useEffect(() => {
        if (agent_params.agent && initialMessage) {
            handleSendMessage(initialMessage, { hideMessage: true })
        }
    }, [agent_params.agent, initialMessage])

    useLayoutEffect(() => {
        const textAreaWrapper = textAreaRef.current;
        if (textAreaWrapper) {
            const textArea = textAreaWrapper.getElementsByTagName('textarea')[0];
            if (textArea) {
                textArea.style.height = "0px";
                const scrollHeight = textArea.scrollHeight;
                textArea.style.height = scrollHeight + "px";
            }
        }
    }, [newMessage]);

    const handleCleanThread = () => {
        chatContext.clearConversation(agentId);
        setAttachment("");
        // Clear streaming refs
        streamingMessagesRef.current.clear();
        streamingChunksRef.current.clear();
        expectedChunksRef.current.clear();
    }

    const setErrorInChat = (errorMessage = "Oops! Something went wrong. Please try again.") => {
        chatContext.setAgentIsTyping(agentId, false);
        const error = {
            message: errorMessage,
            type: "received",
            time: dayjs().format('HH:mm'),
        };
        // Hacer push del error en lugar de reemplazar todo
        const currentMessages = [...messages];
        currentMessages.push(error);
        chatContext.setMessages(agentId, currentMessages);
        chatContext.setShowStopButton(agentId, false);
    }

    const handleSendMessage = async (sentMessage, options = {}) => {
        // Extraer hideMessage de las opciones, por defecto false
        const hideMessage = options.hideMessage || false;
        
        // Determinar qué mensaje se va a enviar
        const messageToSend = sentMessage || newMessage;
        
        if (!messageToSend || messageToSend.trim() === "") {
            return;
        }

        // Verificar conexión antes de enviar
        if (!isConnected) {
            setErrorInChat("No connection to server. Please check your connection and try again.");
            return;
        }

        // ACTIVAR LOADING al inicio del request
        setIsLoading(true);
        chatContext.setIsSendMessageClicked(agentId, true);

        // VARIABLE LOCAL para mantener el estado de los mensajes durante toda la función
        let localMessages = [...messages];

        // AGREGAR EL MENSAJE DEL USUARIO INMEDIATAMENTE (siempre que no esté oculto)
        if (!hideMessage) {
            const userMessage = { 
                message: messageToSend, 
                type: "sent", 
                time: dayjs().format('HH:mm') 
            };
            
            // Agregar el mensaje del usuario a la variable local
            localMessages.push(userMessage);
            
            chatContext.setMessages(agentId, localMessages);
        }

        const params = {
            message_text: messageToSend,
            tenant_id: `6bf81e8b-3041-4f0d-9cbd-52ab9daaf2b9`,
            sender_uuid: '04f88408-6061-4f0d-9dad-1ceaa45307de',
            assistant_uuid: agentId,
            conversation_uuid: conversationID || "", // Usar el conversationID del contexto
            channel: "",
            metadata: JSON.stringify(metadata)
        };

        // Limpiar el input DESPUÉS de guardar el mensaje
        setNewMessage("");

        try {
            // Activar el indicador de typing antes de hacer el request
            chatContext.setAgentIsTyping(agentId, true);
            
            const result = await sendMessage(params, {});
            
            if (result.success) {
                setAttachment("");

                // Update conversationUuid from response
                const newConversationId = result.data?.body?.conversation_uuid || 
                                          result.data?.conversationUuid || 
                                          result.data?.conversation_id;
                
                if (newConversationId) {
                    chatContext.setConversationID(agentId, newConversationId);
                }
                
                // DON'T add assistant response here - wait for chunks from WebSocket
                // The message will be created when the first chunk arrives via WebSocket
                
                // Handle metadata if exists
                if (result.data.metadata && handleMetadata) {
                    handleMetadata(result.data.metadata);
                }
            } else {
                chatContext.setAgentIsTyping(agentId, false);
                chatContext.setIsSendMessageClicked(agentId, false);
                setIsLoading(false);
                setErrorInChat(result.error || "Failed to send message");
            }
        } catch (err) {
            console.error('HTTP request failed:', err);
            chatContext.setAgentIsTyping(agentId, false);
            chatContext.setIsSendMessageClicked(agentId, false);
            setIsLoading(false);
            setErrorInChat(err.message || "Failed to send message");
        }
    };

    // Exponer la función handleSendMessage al componente padre
    useImperativeHandle(ref, () => ({
        sendMessage: handleSendMessage
    }));

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        // Encontrar el contenedor de mensajes con scroll específico de este chat
        const messagesContainer = document.querySelector(`[style*="overflow: auto"][style*="flex: 1"]`);
        if (messagesContainer) {
            // Solo hacer scroll si el usuario no está scrolleando manualmente
            const isNearBottom = messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - 100;
            if (isNearBottom) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
        }
    };

    const handleStopClick = () => {
        // Stop any ongoing streaming
        streamingMessagesRef.current.clear();
        streamingChunksRef.current.clear();
        expectedChunksRef.current.clear();
        chatContext.setAgentIsTyping(agentId, false);
        chatContext.setShowStopButton(agentId, false);
        chatContext.setIsSendMessageClicked(agentId, false);
        setIsLoading(false);
    };

    const handleRetryConnection = async () => {
        const connected = await retryConnection();
        if (connected) {
            setErrorInChat("Connection restored! You can now send messages.");
        }
    };

    // Handler for regenerating a message
    const handleRegenerateMessage = useCallback(async (messageIndex) => {
        if (regeneratingMessageIndex !== null) return; // Already regenerating
        
        // Find the user message that triggered this response
        let userMessageIndex = messageIndex - 1;
        while (userMessageIndex >= 0 && messages[userMessageIndex]?.type !== 'sent') {
            userMessageIndex--;
        }
        
        if (userMessageIndex < 0) return; // No user message found
        
        const userMessage = messages[userMessageIndex];
        setRegeneratingMessageIndex(messageIndex);
        
        // Remove the old response
        const updatedMessages = messages.filter((_, idx) => idx !== messageIndex);
        chatContext.setMessages(agentId, updatedMessages);
        
        // Resend the user message (hidden)
        await handleSendMessage(userMessage.message, { hideMessage: true });
        
        setRegeneratingMessageIndex(null);
    }, [messages, regeneratingMessageIndex, agentId]);

    // Handler for adding a message to chat (used by HomeEnhancedMessage)
    const handleAddChatMessage = useCallback((message) => {
        const newMessage = {
            message: message,
            type: 'sent',
            time: dayjs().format('HH:mm')
        };
        chatContext.setMessages(agentId, [...messages, newMessage]);
    }, [agentId, messages]);

    return (
        <ChatBoxContainer style={{ height: '100vh', maxHeight: '100vh' }}>
            {/* Estilos CSS para la animación del spinner */}
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    @keyframes bounce {
                        0%, 80%, 100% {
                            transform: scale(0);
                            opacity: 0.5;
                        }
                        40% {
                            transform: scale(1);
                            opacity: 1;
                        }
                    }
                `}
            </style>
            
            {/* Mostrar error de conexión si existe */}
            {connectionError && (
                <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#ffebee', 
                    color: '#c62828', 
                    textAlign: 'center',
                    borderBottom: '1px solid #ffcdd2'
                }}>
                    Connection error: {connectionError}
                    <button 
                        onClick={handleRetryConnection}
                        style={{ 
                            marginLeft: '10px', 
                            padding: '5px 10px', 
                            backgroundColor: '#c62828', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '3px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            <ChatMessagesWrapper style={{ flex: 1, overflow: 'auto', minHeight: 0, maxHeight: 'calc(100vh - 400px)' }}>
                <HomePageChatWindow
                    selectedAgent={selectedAgentData}
                    messages={messages}
                    bottomRef={bottomRef}
                    width={'inherit'}
                    height={'100%'}
                    agentIsTyping={agentIsTyping}
                    displayButtons={true}
                    onRegenerateMessage={handleRegenerateMessage}
                    regeneratingMessageIndex={regeneratingMessageIndex}
                    onAddChatMessage={handleAddChatMessage}
                    dataTestId="persistent-chat-window"
                    showAgentName={false}
                />
            </ChatMessagesWrapper>

            <ChatInputWrapper>
                <ChatInput
                    customerId={authStore.selectedAccount.id}
                    onChange={(e) => setNewMessage(e.target.value)}
                    newMessage={newMessage}
                    handleSendMessage={handleSendMessage}
                    onFileUploaded={setAttachment}
                    textAreaRef={textAreaRef}
                    disableSend={isSendMessageClicked || !newMessage.trim() || !isConnected || agentIsTyping}
                    showSpinner={false}
                    onCleanThread={handleCleanThread}
                    showNewChatButton={showNewChatButton}
                    showFileUpload={!agentIsTyping && !isSendMessageClicked}
                    showClearChat={!agentIsTyping && !isSendMessageClicked}
                    stop={showStopButton}
                    onStopClick={handleStopClick}
                    placeholder={"Type your instructions here..."}
                    maxHeight="150px"
                />
            </ChatInputWrapper>
        </ChatBoxContainer>
    );
}));

export default ChatBoxPersistentHTTP;
